import React, { useCallback, useEffect, useState } from 'react';
import {
  ReconcilableAccount,
  SuggestedBalance,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';

interface Props {
  hoaId: string;
  account: ReconcilableAccount;
  onClose: () => void;
  onOpened: (reconciliationId: string) => void;
}

const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * Open a reconciliation against a statement.
 *
 * The closing balance is **typed, and suggested rather than pulled**.
 * The institution does not return it — `statement/find` is metadata
 * only and the figure lives inside the PDF — so the suggestion is the
 * live balance rolled back over the stored feed, and it is offered with
 * its derivation rather than filled in silently.
 *
 * When the backend cannot derive it honestly it says why, and that
 * reason is shown. An empty field with no explanation reads as a bug,
 * and what the accountant does next differs by cause: tracking off is a
 * setting, a short feed is a wait.
 */
const OpenReconciliationDialog: React.FC<Props> = ({
  hoaId,
  account,
  onClose,
  onOpened,
}) => {
  const [statementDate, setStatementDate] = useState(today());
  const [balance, setBalance] = useState('');
  const [suggestion, setSuggestion] = useState<SuggestedBalance | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggest = useCallback(async () => {
    if (!statementDate) return;
    try {
      setSuggesting(true);
      const { data } = await ledgerService.getSuggestedBalance({
        hoaId,
        glAccountId: account.gl_account_id,
        statementDate,
      });
      setSuggestion(data);
    } catch (err) {
      // A bank read failing is not this dialog's problem to report:
      // the figure is typed, and the suggestion is a convenience. Off
      // the cluster it fails by design, because bank egress is
      // IP-whitelisted.
      console.error('Could not suggest a balance:', err);
      setSuggestion(null);
    } finally {
      setSuggesting(false);
    }
  }, [hoaId, account.gl_account_id, statementDate]);

  useEffect(() => {
    void suggest();
  }, [suggest]);

  const submit = async () => {
    try {
      setSaving(true);
      setError(null);
      const { data } = await ledgerService.openReconciliation({
        hoaId,
        reconciliationOpen: {
          gl_account_id: account.gl_account_id,
          statement_date: statementDate,
          statement_balance: balance.trim(),
        },
      });
      onOpened(data.id);
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't open this reconciliation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>
        Reconcile {account.account_number} {account.account_name}
      </DialogTitle>
      <DialogDescription>
        Enter the closing balance from the institution's statement.
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Statement date</Label>
              <Input
                type="date"
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
              />
            </Field>
            <Field>
              <Label>Statement closing balance</Label>
              <Input
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder="0.00"
              />
              <Description>
                Positive is money the association holds.
              </Description>
            </Field>
          </FieldGroup>
        </Fieldset>

        {suggesting && (
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
            Asking the institution for its current balance…
          </p>
        )}

        {!suggesting && suggestion?.suggested_balance && (
          <div className="mt-4 rounded-md bg-zinc-50 dark:bg-zinc-800 p-3 text-sm text-zinc-950 dark:text-white">
            <div className="flex items-center justify-between gap-3">
              <span>
                Suggested:{' '}
                <span className="tabular-nums font-semibold">
                  {formatMoney(suggestion.suggested_balance)}
                </span>
              </span>
              <Button
                plain
                onClick={() => setBalance(suggestion.suggested_balance ?? '')}
              >
                Use this
              </Button>
            </div>
            {/*
              The derivation, stated. It is not authoritative — the
              roll-back trusts the feed to be complete, and testing
              whether the feed is complete is what reconciling IS. So it
              saves the typing without making the claim, and the
              accountant checks it against the PDF.
            */}
            <p className="mt-1 text-zinc-500 dark:text-zinc-400">
              {formatMoney(suggestion.live_balance)} at the institution on{' '}
              {formatBillingDate(suggestion.live_balance_as_of)}, less{' '}
              {suggestion.rolled_back_count} transaction
              {suggestion.rolled_back_count === 1 ? '' : 's'} since the
              statement date ({formatMoney(suggestion.rolled_back_total)}
              ). Check it against the statement.
            </p>
          </div>
        )}

        {!suggesting && suggestion?.unavailable_reason && (
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
            No suggested balance: {suggestion.unavailable_reason}
          </p>
        )}

        <ErrorAlert className="mt-4">{error}</ErrorAlert>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="sky"
          onClick={submit}
          disabled={saving || !balance.trim() || !statementDate}
        >
          {saving ? 'Opening…' : 'Open reconciliation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenReconciliationDialog;
